import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * OrgChart — shared CRM org chart (TekStak + GAS).
 *
 * Built from contacts.reportsToId. Cards are colored by relationship
 * status (the leadership story: where are we strong, where are we blind),
 * badged by influence. Interactive: drag a contact onto another to reparent,
 * or drop onto the "Top level" zone. On mobile/touch, falls back to
 * long-press drag. SVG-based orthogonal connecting lines show hierarchy.
 *
 * Lifecycle (all optional — the chart is read/reparent-only without them):
 * - Bench: contacts with `stashedAt` render in a tray below the tree
 *   ("I know this person, I don't know where they report yet"). Drag a
 *   bench card into the tree to place it; drag a tree card onto the tray
 *   (onStash) to park it.
 * - Delete (onDelete): a ✕ on each card, two-click armed — first click
 *   arms it, second confirms. Soft delete server-side; recoverable.
 * - Merge (onMerge): dropping a card onto a likely duplicate (same last
 *   name or same email) opens a choice — "Reports to" keeps both, "Merge
 *   into" absorbs the dragged card. Dropping onto a bench card is always
 *   a merge (a benched contact can't be a manager).
 * - Provenance (decision #22): every tracked detail (email, phone, LinkedIn,
 *   title, unit, reporting line) may carry a claim — automated (an
 *   assistant wrote it), guessed (inferred, not observed), verified (you
 *   checked it), field (someone in the account confirmed it). Cards wear
 *   one pip per kind; hovering/tapping a pip lists the fields with their
 *   confidence and basis. Verify / Field (onVerify) raise a card's claims
 *   in one click. The bench groups by `orgUnit` so unplaced people still
 *   cluster by where they sit.
 *
 * Presentational: data in via props (the @techstack/core Contact contract
 * field names), persistence out via the callbacks. The component applies
 * moves optimistically and reverts if the callback rejects — the app owns
 * the actual API calls.
 *
 * Styles: import '@techstack/ui/dist/styles/org-chart.css' once per app.
 */
import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors, DragOverlay, useDroppable, useDraggable, } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, ArrowUpToLine, UserPlus, X, Archive, Merge, Pencil, Bot, BadgeCheck, Footprints } from 'lucide-react';
import { Hint } from '../Hint/Hint.js';
export const DEFAULT_RELATIONSHIP_LABELS = {
    champion: 'Champion',
    strong: 'Strong',
    developing: 'Developing',
    new: 'New',
    blocker: 'Blocker',
    dormant: 'Dormant',
};
function buildTree(contacts) {
    const byId = new Map(contacts.map((c) => [c.id, { ...c, children: [] }]));
    const roots = [];
    for (const node of byId.values()) {
        if (node.reportsToId && byId.has(node.reportsToId)) {
            byId.get(node.reportsToId).children.push(node);
        }
        else {
            roots.push(node);
        }
    }
    return roots;
}
/**
 * Build the discovered (ghost) tree from suggestions, parented by
 * suggestedManagerCandidateId. A suggestion whose inferred manager isn't
 * itself in the batch becomes a root of the ghost tree.
 */
function buildGhostTree(suggestions) {
    const byId = new Map(suggestions.map((s) => [s.id, { ...s, key: `g-${s.id}`, children: [], managerName: null }]));
    const roots = [];
    for (const node of byId.values()) {
        const mgr = node.suggestedManagerCandidateId ? byId.get(node.suggestedManagerCandidateId) : null;
        if (mgr) {
            node.managerName = mgr.fullName;
            mgr.children.push(node);
        }
        else {
            roots.push(node);
        }
    }
    return roots;
}
// All descendant ids of a node — a contact can't be moved under their own report
function descendantIds(node, acc = new Set()) {
    for (const child of node.children) {
        acc.add(child.id);
        descendantIds(child, acc);
    }
    return acc;
}
/**
 * Prune the full tree for display: drop `hidden` nodes (focus mode hides
 * off-branch people), and give `collapsed` nodes an empty child list + a count
 * of what they're hiding. Connectors and layout follow the visible tree.
 */
function buildVisibleTree(nodes, collapsed, hidden) {
    const out = [];
    for (const n of nodes) {
        if (hidden.has(n.id))
            continue;
        const isColl = collapsed.has(n.id);
        out.push({
            ...n,
            collapsed: isColl,
            hiddenCount: isColl ? descendantIds(n).size : 0,
            children: isColl ? [] : buildVisibleTree(n.children, collapsed, hidden),
        });
    }
    return out;
}
function fullName(node) {
    return `${node.firstName || ''} ${node.lastName || ''}`.trim();
}
/**
 * Duplicate heuristic gating the merge popover. Deliberately loose — the
 * popover still offers "Reports to", so a false positive costs one click:
 * - same non-empty last name or same non-empty email
 * - same first name when either record is missing a last name — the classic
 *   harvest dupe ("Rodrigo" vs "Rodrigo Munguia", "Josh" vs "Josh Perkins")
 * - one full name is a prefix of the other
 */
function looksLikeDuplicate(a, b) {
    const lastA = (a.lastName || '').trim().toLowerCase();
    const lastB = (b.lastName || '').trim().toLowerCase();
    if (lastA && lastA === lastB)
        return true;
    const emailA = (a.email || '').trim().toLowerCase();
    const emailB = (b.email || '').trim().toLowerCase();
    if (emailA && emailA === emailB)
        return true;
    const firstA = (a.firstName || '').trim().toLowerCase();
    const firstB = (b.firstName || '').trim().toLowerCase();
    if (firstA && firstA === firstB && (!lastA || !lastB))
        return true;
    const nameA = fullName(a).toLowerCase();
    const nameB = fullName(b).toLowerCase();
    if (!nameA || !nameB)
        return false;
    return nameA === nameB || nameA.startsWith(`${nameB} `) || nameB.startsWith(`${nameA} `);
}
const PIP_ORDER = ['guessed', 'automated', 'verified', 'field'];
export const PIP_LABEL = {
    guessed: 'Guessed',
    automated: 'Automated',
    verified: 'Verified',
    field: 'Confirmed in the field',
};
export const PIP_BLURB = {
    guessed: 'Inferred, not observed — a pattern-derived email or a title read off a search result. A lead, not a fact.',
    automated: 'Filled in by an assistant or a pipeline and not yet checked by a person.',
    verified: 'You confirmed this yourself.',
    field: 'Confirmed by someone in the account — a meeting, a signature, a call.',
};
/** Server rank of a claim; a value with no claim is a pre-provenance human entry. */
const PROV_RANK = { automated: 0, verified: 1, field: 2 };
/** The tracked fields, in the server's names, and where each lives on the contact. */
const TRACKED = [
    { field: 'email', label: 'Email', value: (c) => c.email },
    { field: 'phone', label: 'Phone', value: (c) => c.phone },
    { field: 'linkedin_url', label: 'LinkedIn', value: (c) => c.linkedinUrl },
    { field: 'title', label: 'Title', value: (c) => c.title },
    { field: 'org_unit', label: 'Unit', value: (c) => c.orgUnit },
    { field: 'reports_to', label: 'Reports to', value: (c) => c.reportsToId },
];
export const fieldLabel = (field) => TRACKED.find((t) => t.field === field)?.label || field;
/** Which pip a single claim wears. Pure — the contact card reuses it. */
export function claimKind(e) {
    if (!e || !e.status)
        return null;
    if (e.guessed && e.status === 'automated')
        return 'guessed';
    if (e.status === 'automated' || e.status === 'verified' || e.status === 'field')
        return e.status;
    return null;
}
/** What a card wears and what its two actions would do. Pure. */
function summarizeProvenance(c) {
    const fp = c.provenance || {};
    const byKind = { guessed: [], automated: [], verified: [], field: [] };
    const verifyPending = [];
    const fieldPending = [];
    for (const t of TRACKED) {
        const v = t.value(c);
        if (v === null || v === undefined || v === '')
            continue; // nothing to claim
        const e = fp[t.field];
        const status = e && PROV_RANK[String(e.status)] !== undefined ? String(e.status) : null;
        if (e && status) {
            const kind = e.guessed && status === 'automated' ? 'guessed' : status;
            byKind[kind].push({ field: t.field, label: t.label, entry: e });
        }
        const rank = status ? PROV_RANK[status] : null;
        if (rank === null || rank === 0)
            verifyPending.push(t.field);
        if (rank === null || rank < 2)
            fieldPending.push(t.field);
    }
    const assistantAdded = c.source === 'assistant';
    const pips = PIP_ORDER
        .filter((k) => byKind[k].length > 0 || (k === 'automated' && assistantAdded))
        .map((k) => ({ kind: k, entries: byKind[k] }));
    return { pips, verifyPending, fieldPending, assistantAdded };
}
/**
 * One line of a claim's hint: "40% · guessed from account pattern … · by
 * remote-mcp-v2 via claude · Sep 6, 2026". Exported so every surface that
 * shows where a field came from (the chart's pips, the contact card's Info
 * and Sources tabs) says it the same way.
 */
export function describeClaim(e) {
    const parts = [];
    if (e.status === 'automated' && typeof e.confidence === 'number')
        parts.push(`${e.confidence}% confidence`);
    if (e.basis)
        parts.push(e.basis);
    const who = e.confirmed_by
        ? `confirmed by ${e.confirmed_by}`
        : e.actor ? `by ${String(e.actor).replace(/^key:/, '')}` : '';
    const via = e.via ? `via ${e.via}` : '';
    if (who || via)
        parts.push([who, via].filter(Boolean).join(' '));
    if (e.at) {
        const d = new Date(e.at);
        if (!Number.isNaN(d.getTime()))
            parts.push(d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }));
    }
    return parts.join(' · ');
}
function PipGlyph({ kind }) {
    if (kind === 'guessed')
        return _jsx("span", { className: "oc-pip-q", "aria-hidden": "true", children: "?" });
    if (kind === 'automated')
        return _jsx(Bot, { size: 10, "aria-hidden": "true" });
    if (kind === 'verified')
        return _jsx(BadgeCheck, { size: 10, "aria-hidden": "true" });
    return _jsx(Footprints, { size: 10, "aria-hidden": "true" });
}
/**
 * The pips a card wears — one per claim kind, each a Hint trigger listing
 * the fields behind it. Rendered as a SIBLING of the card's clickable
 * content, never inside it: the Hint trigger is a button and the content
 * is role="button". `portal` because cards live in the pan/zoom layer.
 */
function ProvenanceStrip({ summary }) {
    if (summary.pips.length === 0)
        return null;
    return (_jsx("div", { className: "oc-prov", children: summary.pips.map(({ kind, entries }) => (_jsxs(Hint, { portal: true, label: PIP_LABEL[kind], className: `oc-pip oc-pip-${kind}`, ariaLabel: `${PIP_LABEL[kind]}: ${entries.length ? entries.map((e) => e.label).join(', ') : 'added by an assistant'}`, text: (_jsxs(_Fragment, { children: [_jsx("span", { children: PIP_BLURB[kind] }), kind === 'automated' && summary.assistantAdded && (_jsx("span", { className: "hnt-def", children: "Added to the chart by an assistant." })), entries.map((e) => (_jsxs("span", { className: "hnt-def", children: [_jsx("b", { children: e.label }), " ", describeClaim(e.entry)] }, e.field)))] })), children: [_jsx(PipGlyph, { kind: kind }), entries.length > 0 && _jsx("span", { className: "oc-pip-n", children: entries.length })] }, kind))) }));
}
/** One-click Verify / Field, shown only when either would raise something. */
function VerifyActions({ contact, summary, busy, onVerify, }) {
    if (!onVerify)
        return null;
    const canVerify = summary.verifyPending.length > 0;
    const canField = summary.fieldPending.length > 0;
    if (!canVerify && !canField)
        return null;
    const names = (fields) => fields.map((f) => fieldLabel(f).toLowerCase()).join(', ');
    return (_jsxs("div", { className: "oc-verify-actions", children: [canVerify && (_jsxs("button", { type: "button", className: "oc-verify-btn oc-verify-btn-verified", disabled: busy, onClick: (e) => { e.stopPropagation(); onVerify(contact.id, 'verified', summary.verifyPending); }, onDoubleClick: (e) => e.stopPropagation(), title: `Mark as checked by you: ${names(summary.verifyPending)}`, "aria-label": `Mark ${names(summary.verifyPending)} as verified by you`, children: [_jsx(BadgeCheck, { size: 11, "aria-hidden": "true" }), busy ? '…' : 'Verify'] })), canField && (_jsxs("button", { type: "button", className: "oc-verify-btn oc-verify-btn-field", disabled: busy, onClick: (e) => { e.stopPropagation(); onVerify(contact.id, 'field', summary.fieldPending); }, onDoubleClick: (e) => e.stopPropagation(), title: `Confirmed by someone in the account: ${names(summary.fieldPending)}`, "aria-label": `Mark ${names(summary.fieldPending)} as confirmed in the field`, children: [_jsx(Footprints, { size: 11, "aria-hidden": "true" }), busy ? '…' : 'Field'] }))] }));
}
// Root drop zone component
function RootDropZone({ isOver }) {
    const { setNodeRef } = useDroppable({ id: 'root-drop-zone' });
    return (_jsxs("div", { ref: setNodeRef, className: `oc-root-drop ${isOver ? 'oc-root-drop-active' : ''}`, children: [_jsx(ArrowUpToLine, { size: 14 }), _jsx("span", { children: "Drop here to make top-level" })] }));
}
/** Two-click-armed delete ✕ (audit pattern: destructive actions arm first). */
function DeleteX({ contactId, armedDeleteId, onArm, onConfirm, }) {
    const armed = armedDeleteId === contactId;
    return (_jsx("button", { className: `oc-delete-btn ${armed ? 'oc-delete-armed' : ''}`, onClick: (e) => {
            e.stopPropagation();
            if (armed)
                onConfirm(contactId);
            else
                onArm(contactId);
        }, title: armed ? 'Click again to delete (recoverable from trash)' : 'Delete contact', children: armed ? 'Sure?' : _jsx(X, { size: 12 }) }));
}
// Draggable and droppable contact card
function ContactCard({ node, activeId, overId, focusId, blocked, relationshipLabels, armedDeleteId, onArmDelete, onDelete, onAddContact, onContactClick, onToggleCollapse, onFocus, verifyBusy, onVerify, }) {
    const isDragging = activeId === node.id;
    const prov = useMemo(() => summarizeProvenance(node), [node]);
    const { attributes, listeners, setNodeRef: setDragRef, transform, } = useDraggable({ id: node.id, data: { node } });
    const { setNodeRef: setDropRef, isOver } = useDroppable({
        id: `drop-${node.id}`,
        data: { node },
        disabled: blocked.has(node.id),
    });
    // Combine refs for both drag and drop
    const setNodeRef = (el) => {
        setDragRef(el);
        setDropRef(el);
    };
    const style = transform
        ? { transform: CSS.Translate.toString(transform), opacity: isDragging ? 0.5 : 1 }
        : undefined;
    return (_jsxs("li", { className: "oc-branch", children: [_jsxs("div", { ref: setNodeRef, "data-contact-id": node.id, style: style, className: [
                    'oc-card',
                    `oc-rel-${node.relationship || 'none'}`,
                    isDragging ? 'oc-card-dragging' : '',
                    isOver && !blocked.has(node.id) ? 'oc-card-drop-target' : '',
                    overId === `drop-${node.id}` ? 'oc-card-highlight' : '',
                    focusId === node.id ? 'oc-card-focused' : '',
                ].join(' '), children: [_jsx("div", { className: "oc-drag-handle", ...listeners, ...attributes, children: _jsx(GripVertical, { size: 12 }) }), _jsxs("div", { className: "oc-card-main", children: [_jsxs("div", { className: "oc-card-content", 
                                // Single click drills into (focuses) this person's branch; double
                                // click opens the editor. Keyboard: Enter/Space edits (a11y).
                                onClick: () => onFocus(node), onDoubleClick: () => onContactClick?.(node), role: "button", tabIndex: 0, onKeyDown: (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        (onContactClick || onFocus)(node);
                                    }
                                }, children: [_jsxs("div", { className: "oc-card-top", children: [_jsx("span", { className: "oc-name", children: fullName(node) }), node.influence === 'decision_maker' && (_jsx("span", { className: "oc-badge oc-badge-dm", children: "DM" })), node.influence === 'blocker' && (_jsx("span", { className: "oc-badge oc-badge-block", children: "!" }))] }), _jsx("span", { className: "oc-title", children: node.title || 'Title unknown' }), node.relationship && (_jsx("span", { className: "oc-rel-label", children: relationshipLabels[node.relationship] || node.relationship }))] }), _jsx(ProvenanceStrip, { summary: prov })] }), _jsx(VerifyActions, { contact: node, summary: prov, busy: verifyBusy.has(node.id), onVerify: onVerify }), _jsxs("div", { className: "oc-card-actions", children: [onContactClick && (_jsx("button", { className: "oc-edit-btn", onClick: (e) => {
                                    e.stopPropagation();
                                    onContactClick(node);
                                }, title: "Edit contact", "aria-label": "Edit contact", children: _jsx(Pencil, { size: 12 }) })), onDelete && (_jsx(DeleteX, { contactId: node.id, armedDeleteId: armedDeleteId, onArm: onArmDelete, onConfirm: onDelete })), onAddContact && (_jsx("button", { className: "oc-add-child-btn", onClick: (e) => {
                                    e.stopPropagation();
                                    onAddContact(node);
                                }, title: "Add report", children: _jsx(UserPlus, { size: 12 }) }))] }), (node.collapsed ? node.hiddenCount > 0 : node.children.length > 0) && (_jsx("button", { type: "button", className: `oc-collapse-toggle ${node.collapsed ? 'oc-collapse-on' : ''}`, onClick: (e) => { e.stopPropagation(); onToggleCollapse(node.id); }, onDoubleClick: (e) => e.stopPropagation(), title: node.collapsed ? `Expand ${node.hiddenCount} report${node.hiddenCount === 1 ? '' : 's'}` : 'Collapse reports', "aria-label": node.collapsed ? 'Expand reports' : 'Collapse reports', children: node.collapsed ? `+${node.hiddenCount}` : '−' }))] }), node.children.length > 0 && (_jsx("ul", { className: "oc-children", children: node.children.map((child) => (_jsx(ContactCard, { node: child, activeId: activeId, overId: overId, focusId: focusId, blocked: blocked, relationshipLabels: relationshipLabels, armedDeleteId: armedDeleteId, onArmDelete: onArmDelete, onDelete: onDelete, onAddContact: onAddContact, onContactClick: onContactClick, onToggleCollapse: onToggleCollapse, onFocus: onFocus, verifyBusy: verifyBusy, onVerify: onVerify }, child.id))) }))] }));
}
/** Compact draggable card in the bench tray (also a merge drop target). */
function BenchCard({ contact, activeId, armedDeleteId, onArmDelete, onDelete, onContactClick, verifyBusy, onVerify, }) {
    const isDragging = activeId === contact.id;
    const prov = useMemo(() => summarizeProvenance(contact), [contact]);
    const { attributes, listeners, setNodeRef: setDragRef, transform } = useDraggable({
        id: contact.id,
        data: { node: contact },
    });
    const { setNodeRef: setDropRef, isOver } = useDroppable({
        id: `drop-${contact.id}`,
        data: { node: contact },
        disabled: isDragging,
    });
    const setNodeRef = (el) => {
        setDragRef(el);
        setDropRef(el);
    };
    const style = transform
        ? { transform: CSS.Translate.toString(transform), opacity: isDragging ? 0.5 : 1 }
        : undefined;
    return (_jsxs("div", { ref: setNodeRef, style: style, className: [
            'oc-bench-card',
            isDragging ? 'oc-card-dragging' : '',
            isOver && !isDragging ? 'oc-card-drop-target' : '',
        ].join(' '), children: [_jsx("div", { className: "oc-drag-handle", ...listeners, ...attributes, children: _jsx(GripVertical, { size: 12 }) }), _jsxs("div", { className: "oc-bench-card-main", children: [_jsxs("div", { className: `oc-bench-card-content ${onContactClick ? 'oc-bench-card-clickable' : ''}`, onClick: () => onContactClick?.(contact), children: [_jsx("span", { className: "oc-name", children: fullName(contact) }), contact.title && _jsx("span", { className: "oc-title", children: contact.title })] }), _jsx(ProvenanceStrip, { summary: prov })] }), _jsx(VerifyActions, { contact: contact, summary: prov, busy: verifyBusy.has(contact.id), onVerify: onVerify }), onDelete && (_jsx(DeleteX, { contactId: contact.id, armedDeleteId: armedDeleteId, onArm: onArmDelete, onConfirm: onDelete }))] }));
}
/** Bench tray: droppable target + the parked cards. */
function BenchTray({ benched, isOver, droppable, activeId, armedDeleteId, onArmDelete, onDelete, onContactClick, verifyBusy, onVerify, }) {
    const { setNodeRef } = useDroppable({ id: 'bench-drop-zone', disabled: !droppable });
    // Grouped by the person's own unit so unplaced people still cluster by
    // where they sit; units A→Z, people without one last. A bench where
    // nobody has a unit stays the flat tray it always was.
    const groups = useMemo(() => {
        const byUnit = new Map();
        for (const c of benched) {
            const k = (c.orgUnit || '').trim();
            const arr = byUnit.get(k) || [];
            arr.push(c);
            byUnit.set(k, arr);
        }
        const named = [...byUnit.keys()].filter(Boolean).sort((a, b) => a.localeCompare(b));
        const keys = byUnit.has('') ? [...named, ''] : named;
        return keys.map((unit) => ({ unit, contacts: byUnit.get(unit) }));
    }, [benched]);
    const grouped = groups.some((g) => g.unit !== '');
    const renderCard = (c) => (_jsx(BenchCard, { contact: c, activeId: activeId, armedDeleteId: armedDeleteId, onArmDelete: onArmDelete, onDelete: onDelete, onContactClick: onContactClick, verifyBusy: verifyBusy, onVerify: onVerify }, c.id));
    return (_jsxs("div", { ref: setNodeRef, className: [
            'oc-bench',
            isOver ? 'oc-bench-active' : '',
            droppable && activeId ? 'oc-bench-droppable' : '',
        ].join(' '), children: [_jsxs("div", { className: "oc-bench-header", children: [_jsx(Archive, { size: 13 }), _jsxs("span", { children: ["Bench", benched.length ? ` (${benched.length})` : '', " \u2014 parked until you know where they report"] })] }), benched.length > 0 && grouped ? (_jsx("div", { className: "oc-bench-groups", children: groups.map((g) => (_jsxs("section", { className: `oc-bench-group ${g.unit ? '' : 'oc-bench-group-none'}`, children: [_jsxs("h4", { className: "oc-bench-group-head", children: [_jsx("span", { className: "oc-bench-group-unit", children: g.unit || '(no unit)' }), _jsx("span", { className: "oc-bench-group-n", children: g.contacts.length })] }), _jsx("div", { className: "oc-bench-cards", children: g.contacts.map(renderCard) })] }, g.unit || '__none__'))) })) : benched.length > 0 ? (_jsx("div", { className: "oc-bench-cards", children: benched.map(renderCard) })) : (_jsx("div", { className: "oc-bench-empty", children: droppable ? 'Drag a contact here to park it' : 'Empty' }))] }));
}
// Drag overlay card (shown while dragging)
function DragOverlayCard({ node }) {
    return (_jsx("div", { className: `oc-card oc-card-overlay oc-rel-${node.relationship || 'none'}`, children: _jsxs("div", { className: "oc-card-content", children: [_jsxs("div", { className: "oc-card-top", children: [_jsx("span", { className: "oc-name", children: fullName(node) }), node.influence === 'decision_maker' && (_jsx("span", { className: "oc-badge oc-badge-dm", children: "DM" })), node.influence === 'blocker' && (_jsx("span", { className: "oc-badge oc-badge-block", children: "!" }))] }), _jsx("span", { className: "oc-title", children: node.title || 'Title unknown' })] }) }));
}
// SVG Connector Lines component - draws orthogonal lines between parent and children
function ConnectorLines({ containerRef, tree, zoom = 1, dashed = false, }) {
    const [lines, setLines] = useState([]);
    const [size, setSize] = useState(null);
    const calculateLines = useCallback(() => {
        if (!containerRef.current)
            return;
        const container = containerRef.current;
        const containerRect = container.getBoundingClientRect();
        const z = zoom || 1;
        // CONTENT-space coordinates. The container is the transformed canvas layer,
        // so measured rects reflect the current translate+scale. Subtracting the
        // container's own (transformed) origin and dividing by the scale yields
        // stable content coords; the SVG lives inside the same transform, so it
        // scales with the cards and never needs re-measuring on pan/zoom.
        const cx = (r) => (r.left - containerRect.left) / z;
        const cyTop = (r) => (r.top - containerRect.top) / z;
        const cyBottom = (r) => (r.bottom - containerRect.top) / z;
        const cw = (r) => r.width / z;
        const newLines = [];
        // Recursively find all parent-child connections
        const processNode = (node) => {
            if (node.children.length === 0)
                return;
            const parentEl = container.querySelector(`[data-contact-id="${node.id}"]`);
            if (!parentEl)
                return;
            const parentRect = parentEl.getBoundingClientRect();
            const parentCenterX = cx(parentRect) + cw(parentRect) / 2;
            const parentBottomY = cyBottom(parentRect);
            // Get all child positions
            const childPositions = [];
            for (const child of node.children) {
                const childEl = container.querySelector(`[data-contact-id="${child.id}"]`);
                if (childEl) {
                    const childRect = childEl.getBoundingClientRect();
                    childPositions.push({
                        x: cx(childRect) + cw(childRect) / 2,
                        y: cyTop(childRect),
                    });
                }
            }
            if (childPositions.length === 0)
                return;
            // Vertical spacing for the connector
            const vertGap = 20;
            // Draw vertical line from parent to horizontal rail
            const railY = parentBottomY + vertGap;
            newLines.push({
                x1: parentCenterX,
                y1: parentBottomY,
                x2: parentCenterX,
                y2: railY,
                key: `${node.id}-down`,
            });
            if (childPositions.length === 1) {
                // Single child: straight line down
                newLines.push({
                    x1: parentCenterX,
                    y1: railY,
                    x2: childPositions[0].x,
                    y2: childPositions[0].y,
                    key: `${node.id}-to-${node.children[0].id}`,
                });
            }
            else {
                // Multiple children: horizontal rail + vertical drops
                const minX = Math.min(...childPositions.map((p) => p.x));
                const maxX = Math.max(...childPositions.map((p) => p.x));
                // Horizontal rail
                newLines.push({
                    x1: minX,
                    y1: railY,
                    x2: maxX,
                    y2: railY,
                    key: `${node.id}-rail`,
                });
                // Vertical lines to each child
                childPositions.forEach((pos, i) => {
                    newLines.push({
                        x1: pos.x,
                        y1: railY,
                        x2: pos.x,
                        y2: pos.y,
                        key: `${node.id}-to-${node.children[i].id}`,
                    });
                });
            }
            // Process children recursively
            for (const child of node.children) {
                processNode(child);
            }
        };
        for (const root of tree) {
            processNode(root);
        }
        setLines(newLines);
        // Cover the whole scrollable content, not just the visible box — a
        // 100%-wide SVG clips connectors on wide trees. scrollWidth is layout
        // (content) size — unaffected by the canvas transform.
        setSize({ w: container.scrollWidth, h: container.scrollHeight });
    }, [containerRef, tree, zoom]);
    useEffect(() => {
        calculateLines();
        // Recalculate on window resize
        const handleResize = () => setTimeout(calculateLines, 100);
        window.addEventListener('resize', handleResize);
        // ...and on any layout change inside the chart (toasts pushing content,
        // fonts settling, cards wrapping, fullscreen toggles). The fixed 50 ms
        // timer alone raced real layout and left lines floating mid-air.
        let ro;
        if (typeof ResizeObserver !== 'undefined' && containerRef.current) {
            ro = new ResizeObserver(() => calculateLines());
            ro.observe(containerRef.current);
            const treeEl = containerRef.current.querySelector('.oc-tree');
            if (treeEl)
                ro.observe(treeEl);
        }
        return () => {
            window.removeEventListener('resize', handleResize);
            if (ro)
                ro.disconnect();
        };
    }, [calculateLines]);
    // Also recalculate when tree changes
    useEffect(() => {
        const timer = setTimeout(calculateLines, 50);
        return () => clearTimeout(timer);
    }, [tree, calculateLines]);
    if (lines.length === 0)
        return null;
    return (_jsx("svg", { className: "oc-svg-lines", style: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: size ? `${size.w}px` : '100%',
            height: size ? `${size.h}px` : '100%',
            pointerEvents: 'none',
        }, children: lines.map((line) => (_jsx("line", { x1: line.x1, y1: line.y1, x2: line.x2, y2: line.y2, className: dashed ? 'oc-svg-connector oc-svg-connector-ghost' : 'oc-svg-connector' }, line.key))) }));
}
/* ─────────────────────── Suggestions (staging rail) ─────────────────────── */
const SENIORITY_LABEL = {
    exec: 'Exec',
    director: 'Director',
    manager: 'Manager',
    ic: 'IC',
};
/** Sort order for the rail — most senior first. */
const SENIORITY_ORDER = { exec: 0, director: 1, manager: 2, ic: 3 };
/** One compact suggestion row in the staging rail. */
function SuggestionRow({ s, busy, onConfirm, onMerge, onReject, onBench, }) {
    const isMatched = !!s.matchedContactId;
    const conf = typeof s.confidence === 'number' ? s.confidence : null;
    const isLiSearch = !!s.linkedinUrl && /\/search\//.test(s.linkedinUrl);
    return (_jsxs("div", { className: `oc-rail-row ${isMatched ? 'oc-rail-row-matched' : ''}`, children: [_jsxs("div", { className: "oc-rail-body", children: [_jsxs("div", { className: "oc-rail-name-row", children: [_jsx("span", { className: "oc-rail-name", children: s.fullName }), s.seniorityLevel && SENIORITY_LABEL[s.seniorityLevel] && (_jsx("span", { className: "oc-badge oc-badge-suggested", children: SENIORITY_LABEL[s.seniorityLevel] }))] }), _jsx("span", { className: "oc-rail-title", children: s.title || 'Title unknown' }), _jsxs("div", { className: "oc-rail-meta", children: [conf !== null && _jsxs("span", { className: "oc-rail-conf", children: [conf, "%"] }), s.sourceUrl && (_jsx("a", { href: s.sourceUrl, target: "_blank", rel: "noopener noreferrer", className: "oc-suggested-link", title: s.sourceName || 'Source', children: "source" })), s.linkedinUrl && (_jsx("a", { href: s.linkedinUrl, target: "_blank", rel: "noopener noreferrer", className: "oc-suggested-link", children: isLiSearch ? 'LinkedIn↗' : 'LinkedIn' }))] }), isMatched && (_jsxs("span", { className: "oc-rail-match", title: "Matches an existing CRM contact", children: ["\u2194 matches ", s.matchedContactName || 'existing contact'] }))] }), _jsxs("div", { className: "oc-rail-row-actions", children: [isMatched && onMerge ? (_jsx("button", { type: "button", className: "oc-sg-btn oc-sg-merge", disabled: busy, onClick: () => onMerge(s.id), children: busy ? '…' : 'Merge' })) : (onConfirm && (_jsx("button", { type: "button", className: "oc-sg-btn oc-sg-confirm", disabled: busy, onClick: () => onConfirm(s.id), children: busy ? '…' : 'Confirm' }))), onBench && !isMatched && (_jsx("button", { type: "button", className: "oc-sg-btn oc-sg-bench", disabled: busy, onClick: () => onBench(s.id), title: "Validate as real, park on the bench", children: busy ? '…' : 'Bench' })), onReject && (_jsx("button", { type: "button", className: "oc-sg-btn oc-sg-reject", disabled: busy, onClick: () => onReject(s.id), title: "Reject suggestion", children: "\u2715" }))] })] }));
}
/**
 * Staging rail — discovered people listed compactly BESIDE the canvas (not
 * crammed as a second tree above it). Triage from here; confirming drops a
 * person onto the chart. Collapsible, sorted most-senior-first.
 */
function SuggestionRail({ suggestions, onConfirm, onMerge, onReject, onBench, onAcceptAll, }) {
    const [collapsed, setCollapsed] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [acceptingAll, setAcceptingAll] = useState(false);
    const [error, setError] = useState(null);
    const acceptAll = () => {
        if (!onAcceptAll)
            return;
        setAcceptingAll(true);
        setError(null);
        Promise.resolve(onAcceptAll())
            .catch((e) => setError(e instanceof Error ? e.message : String(e)))
            .finally(() => setAcceptingAll(false));
    };
    const run = (id, fn) => {
        if (!fn)
            return;
        setBusyId(id);
        setError(null);
        Promise.resolve(fn(id))
            .catch((e) => setError(e instanceof Error ? e.message : String(e)))
            .finally(() => setBusyId(null));
    };
    const sorted = useMemo(() => [...suggestions].sort((a, b) => {
        const sa = SENIORITY_ORDER[a.seniorityLevel || 'ic'] ?? 3;
        const sb = SENIORITY_ORDER[b.seniorityLevel || 'ic'] ?? 3;
        if (sa !== sb)
            return sa - sb;
        return (b.confidence || 0) - (a.confidence || 0);
    }), [suggestions]);
    if (suggestions.length === 0)
        return null;
    return (_jsxs("aside", { className: `oc-rail ${collapsed ? 'oc-rail-collapsed' : ''}`, children: [_jsxs("button", { type: "button", className: "oc-rail-head", onClick: () => setCollapsed((c) => !c), title: collapsed ? 'Expand suggestions' : 'Collapse suggestions', children: [_jsx("span", { className: "oc-rail-chevron", children: collapsed ? '▸' : '▾' }), _jsx("span", { className: "oc-rail-heading", children: "Suggested" }), _jsx("span", { className: "oc-suggestions-count", children: suggestions.length })] }), !collapsed && (_jsxs("div", { className: "oc-rail-inner", children: [onAcceptAll && suggestions.length > 1 && (_jsx("button", { type: "button", className: "oc-rail-accept-all", disabled: acceptingAll, onClick: acceptAll, title: "Build the whole discovered chart \u2014 creates every person and wires the reporting lines", children: acceptingAll ? 'Building chart…' : `Add all ${suggestions.length} to chart` })), _jsx("p", { className: "oc-rail-intro", children: "Add all builds the discovered structure at once. Or handle one at a time \u2014 confirm, merge into an existing contact, or reject." }), error && _jsx("div", { className: "oc-error", children: error }), _jsx("div", { className: "oc-rail-list", children: sorted.map((s) => (_jsx(SuggestionRow, { s: s, busy: busyId === s.id, onConfirm: onConfirm ? (id) => run(id, onConfirm) : undefined, onMerge: onMerge ? (id) => run(id, onMerge) : undefined, onReject: onReject ? (id) => run(id, onReject) : undefined, onBench: onBench ? (id) => run(id, onBench) : undefined }, s.id))) })] }))] }));
}
/**
 * A ghosted, provisional node in the discovered-structure tree, rendered ON the
 * canvas so the org is visible in context BEFORE anything is committed. Dashed +
 * muted so it never reads as a confirmed contact. Draggable (when placement is
 * wired) onto a real manager to place it; inline Add/Merge/✕ to act in place.
 */
function GhostCard({ node, draggable, activeId, busyId, onConfirm, onMerge, onReject, onBench, }) {
    const isDragging = activeId === node.key;
    const { attributes, listeners, setNodeRef, transform } = useDraggable({
        id: node.key,
        data: { ghostId: node.id },
        disabled: !draggable,
    });
    const style = transform
        ? { transform: CSS.Translate.toString(transform), opacity: isDragging ? 0.4 : undefined }
        : undefined;
    const conf = typeof node.confidence === 'number' ? node.confidence : null;
    const low = conf !== null && conf < 60;
    const isMatched = !!node.matchedContactId;
    const busy = busyId === node.id;
    return (_jsxs("li", { className: "oc-branch", children: [_jsxs("div", { ref: setNodeRef, "data-contact-id": node.key, style: style, className: [
                    'oc-card',
                    'oc-ghost-card',
                    low ? 'oc-ghost-low' : '',
                    isMatched ? 'oc-ghost-matched' : '',
                    isDragging ? 'oc-card-dragging' : '',
                ].join(' '), children: [draggable && (_jsx("div", { className: "oc-drag-handle", ...listeners, ...attributes, title: "Drag onto a manager to place", children: _jsx(GripVertical, { size: 12 }) })), _jsxs("div", { className: "oc-card-content", children: [_jsxs("div", { className: "oc-card-top", children: [_jsx("span", { className: "oc-name", children: node.fullName }), node.seniorityLevel && SENIORITY_LABEL[node.seniorityLevel] && (_jsx("span", { className: "oc-badge oc-badge-suggested", children: SENIORITY_LABEL[node.seniorityLevel] }))] }), _jsx("span", { className: "oc-title", children: node.title || 'Title unknown' }), _jsxs("div", { className: "oc-ghost-reports", children: [node.managerName ? (_jsxs("span", { className: "oc-ghost-reports-to", title: "Inferred reporting line \u2014 drag onto a real manager to change", children: ["\u21B3 ", node.managerName] })) : (_jsx("span", { className: "oc-ghost-reports-top", children: "top of discovered chart" })), conf !== null && (_jsxs("span", { className: `oc-ghost-conf ${low ? 'oc-ghost-conf-low' : ''}`, title: "Discovery confidence", children: [conf, "%"] }))] }), isMatched && (_jsxs("span", { className: "oc-ghost-match", title: "Matches an existing CRM contact", children: ["\u2194 ", node.matchedContactName || 'existing contact'] }))] }), _jsxs("div", { className: "oc-card-actions oc-ghost-actions", children: [isMatched && onMerge ? (_jsx("button", { type: "button", className: "oc-sg-btn oc-sg-merge", disabled: busy, onClick: () => onMerge(node.id), title: "Merge into the existing contact", children: busy ? '…' : 'Merge' })) : (onConfirm && (_jsx("button", { type: "button", className: "oc-sg-btn oc-sg-confirm", disabled: busy, onClick: () => onConfirm(node.id), title: "Add as a real contact (unplaced)", children: busy ? '…' : 'Add' }))), onBench && !isMatched && (_jsx("button", { type: "button", className: "oc-sg-btn oc-sg-bench", disabled: busy, onClick: () => onBench(node.id), title: "Validate as a real person but park on the bench \u2014 reporting line unknown for now", children: busy ? '…' : 'Bench' })), onReject && (_jsx("button", { type: "button", className: "oc-sg-btn oc-sg-reject", disabled: busy, onClick: () => onReject(node.id), title: "Dismiss suggestion", children: "\u2715" }))] })] }), node.children.length > 0 && (_jsx("ul", { className: "oc-children", children: node.children.map((child) => (_jsx(GhostCard, { node: child, draggable: draggable, activeId: activeId, busyId: busyId, onConfirm: onConfirm, onMerge: onMerge, onReject: onReject, onBench: onBench }, child.key))) }))] }));
}
/** Drag overlay for a ghost card (shown while dragging a discovered person). */
function GhostOverlayCard({ s }) {
    return (_jsx("div", { className: "oc-card oc-ghost-card oc-card-overlay", children: _jsxs("div", { className: "oc-card-content", children: [_jsx("div", { className: "oc-card-top", children: _jsx("span", { className: "oc-name", children: s.fullName }) }), _jsx("span", { className: "oc-title", children: s.title || 'Title unknown' })] }) }));
}
export function OrgChart({ contacts, suggestions = [], onConfirmSuggestion, onMergeSuggestion, onRejectSuggestion, onAcceptAllSuggestions, onPlaceSuggestion, onBenchSuggestion, onReparent, onDelete, onStash, onMerge, onVerify, onAddContact, onContactClick, fullscreen = false, relationshipLabels = DEFAULT_RELATIONSHIP_LABELS, focusRequest = null, }) {
    const [activeId, setActiveId] = useState(null);
    const [overId, setOverId] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [armedDeleteId, setArmedDeleteId] = useState(null);
    const [pendingDrop, setPendingDrop] = useState(null);
    const containerRef = useRef(null); // viewport (.oc-scroll)
    const canvasRef = useRef(null); // transformed layer (.oc-canvas)
    // Navigable canvas: pan/zoom so a 20-30 node org is a place you move around,
    // not an overflow that drops cards off the edge. The transform lives on the
    // canvas layer; connectors are measured zoom-invariant so they stay glued.
    const [view, setView] = useState({ x: 24, y: 16, z: 1 });
    // Mirror of `view` for synchronous reads inside gesture handlers (pointer/wheel
    // fire between renders — the closured `view` can be a frame stale).
    const viewRef = useRef(view);
    viewRef.current = view;
    const panRef = useRef(null);
    // Active pointers on the background (for touch pinch/two-finger pan) + the
    // in-flight pinch anchor. A gesture reads from a frozen startView so it never
    // drifts (each move recomputes from the start, not incrementally).
    const pointersRef = useRef(new Map());
    const pinchRef = useRef(null);
    const [panning, setPanning] = useState(false);
    const [dense, setDense] = useState(false); // compact card density for big charts
    const [suggBusyId, setSuggBusyId] = useState(null); // ghost-card action in flight
    // Run a suggestion callback (confirm/merge/reject/place) with a per-card busy
    // flag and inline error surfacing — used by the on-canvas ghost cards.
    const runSuggestion = (id, fn) => {
        if (!fn)
            return;
        setSuggBusyId(id);
        setError(null);
        Promise.resolve(fn(id))
            .catch((e) => setError(e instanceof Error ? e.message : String(e)))
            .finally(() => setSuggBusyId(null));
    };
    const placeSuggestion = (suggestionId, managerId) => {
        if (!onPlaceSuggestion)
            return;
        setSuggBusyId(suggestionId);
        setError(null);
        Promise.resolve(onPlaceSuggestion(suggestionId, managerId))
            .catch((e) => setError(e instanceof Error ? e.message : String(e)))
            .finally(() => setSuggBusyId(null));
    };
    const benchSuggestion = (suggestionId) => {
        if (!onBenchSuggestion)
            return;
        setSuggBusyId(suggestionId);
        setError(null);
        Promise.resolve(onBenchSuggestion(suggestionId))
            .catch((e) => setError(e instanceof Error ? e.message : String(e)))
            .finally(() => setSuggBusyId(null));
    };
    const clampZoom = (z) => Math.min(2, Math.max(0.25, z));
    // Collapse/expand + focus so a big org fits the screen and clicking a person
    // drills into their branch.
    const [collapsed, setCollapsed] = useState(new Set());
    const [focusId, setFocusId] = useState(null);
    // Fluid, eased view transitions for PROGRAMMATIC moves (fit / zoom buttons /
    // focus / reset). Direct manipulation (wheel/pinch/pan) stays instant — you
    // don't animate a drag. Honors prefers-reduced-motion.
    const animRef = useRef(0);
    const animTimerRef = useRef(undefined);
    const reduceMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const animateViewTo = useCallback((target, ms = 380) => {
        cancelAnimationFrame(animRef.current);
        if (animTimerRef.current)
            clearTimeout(animTimerRef.current);
        if (ms <= 0 || reduceMotion()) {
            setView(target);
            return;
        }
        const start = { ...viewRef.current };
        const t0 = performance.now();
        const ease = (t) => 1 - Math.pow(1 - t, 3); // easeOutCubic
        const step = (now) => {
            const p = Math.min(1, (now - t0) / ms);
            const e = ease(p);
            setView({
                x: start.x + (target.x - start.x) * e,
                y: start.y + (target.y - start.y) * e,
                z: start.z + (target.z - start.z) * e,
            });
            if (p < 1)
                animRef.current = requestAnimationFrame(step);
        };
        animRef.current = requestAnimationFrame(step);
        // Convergence guarantee: if rAF is throttled/paused (e.g. a background tab),
        // still land exactly on target so the control never looks dead. Harmless
        // no-op when the animation completed normally.
        animTimerRef.current = setTimeout(() => setView(target), ms + 80);
    }, []);
    useEffect(() => () => {
        cancelAnimationFrame(animRef.current);
        if (animTimerRef.current)
            clearTimeout(animTimerRef.current);
    }, []);
    // Zoom by `factor` keeping the CONTENT point under (clientX, clientY) fixed —
    // the industry-standard "zoom toward the cursor/pinch". Buttons pass the
    // viewport center; wheel/pinch pass the cursor/midpoint.
    const zoomAt = useCallback((clientX, clientY, factor) => {
        setView((v) => {
            const vp = containerRef.current;
            const nz = clampZoom(v.z * factor);
            if (!vp || nz === v.z)
                return { ...v, z: nz };
            const rect = vp.getBoundingClientRect();
            const px = clientX - rect.left; // anchor point in viewport coords
            const py = clientY - rect.top;
            const k = nz / v.z;
            return { z: nz, x: px - (px - v.x) * k, y: py - (py - v.y) * k };
        });
    }, []);
    // Toolbar +/− : animate a zoom step toward the viewport center (fluid).
    const zoomBy = (factor) => {
        const vp = containerRef.current;
        const v = viewRef.current;
        const nz = clampZoom(v.z * factor);
        if (nz === v.z)
            return;
        const cxp = vp ? vp.clientWidth / 2 : 0;
        const cyp = vp ? vp.clientHeight / 2 : 0;
        const k = nz / v.z;
        animateViewTo({ z: nz, x: cxp - (cxp - v.x) * k, y: cyp - (cyp - v.y) * k }, 200);
    };
    const resetView = () => animateViewTo({ x: 24, y: 16, z: 1 }, 300);
    // Target view that fits the currently-VISIBLE content in the viewport.
    const computeFit = useCallback(() => {
        const vp = containerRef.current;
        const canvas = canvasRef.current;
        const contentEl = canvas?.querySelector('.oc-canvas-content') ||
            canvas?.querySelector('.oc-tree');
        if (!vp || !contentEl || vp.clientWidth < 40)
            return null; // not laid out yet
        const cw = contentEl.scrollWidth; // content size (transform-immune)
        const ch = contentEl.scrollHeight;
        if (cw < 4 || ch < 4)
            return null;
        const pad = 40;
        const z = clampZoom(Math.min((vp.clientWidth - pad) / cw, (vp.clientHeight - pad) / ch, 1));
        return { z, x: (vp.clientWidth - cw * z) / 2, y: Math.max(12, (vp.clientHeight - ch * z) / 2) };
    }, []);
    const fitView = useCallback((animate = true) => {
        const target = computeFit();
        if (target)
            (animate ? animateViewTo(target, 380) : setView(target));
    }, [computeFit, animateViewTo]);
    // Pan/pinch on the empty canvas background (cards use their own drag handle).
    // One pointer = pan; two pointers = pinch-zoom + two-finger pan (touch).
    const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
    const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
    const onCanvasPointerDown = (e) => {
        const t = e.target;
        if (t.closest('.oc-card, .oc-drag-handle, .oc-card-actions, button, a, input'))
            return;
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointersRef.current.size === 1) {
            panRef.current = { startX: e.clientX, startY: e.clientY, ox: viewRef.current.x, oy: viewRef.current.y };
            setPanning(true);
        }
        else if (pointersRef.current.size === 2) {
            // Second finger down — switch from pan to pinch, anchored on the current view.
            panRef.current = null;
            const [p1, p2] = [...pointersRef.current.values()];
            pinchRef.current = { startDist: dist(p1, p2) || 1, startMid: mid(p1, p2), startView: viewRef.current };
        }
        // Capture last (and defensively) — it must never gate the gesture state above.
        try {
            e.currentTarget.setPointerCapture?.(e.pointerId);
        }
        catch { /* non-active pointer */ }
    };
    const onCanvasPointerMove = (e) => {
        if (!pointersRef.current.has(e.pointerId))
            return;
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        const anchor = pinchRef.current;
        if (pointersRef.current.size >= 2 && anchor) {
            // Pinch: zoom from the frozen startView by the finger-distance ratio,
            // anchored on the initial midpoint, then translate by midpoint drift.
            const [p1, p2] = [...pointersRef.current.values()];
            const d = dist(p1, p2);
            const m = mid(p1, p2);
            const vp = containerRef.current;
            const rect = vp?.getBoundingClientRect();
            const s = anchor;
            const nz = clampZoom(s.startView.z * (d / s.startDist));
            const k = nz / s.startView.z;
            const smx = s.startMid.x - (rect?.left || 0);
            const smy = s.startMid.y - (rect?.top || 0);
            setView({
                z: nz,
                x: smx - (smx - s.startView.x) * k + (m.x - s.startMid.x),
                y: smy - (smy - s.startView.y) * k + (m.y - s.startMid.y),
            });
            return;
        }
        const p = panRef.current;
        if (!p)
            return;
        setView((v) => ({ ...v, x: p.ox + (e.clientX - p.startX), y: p.oy + (e.clientY - p.startY) }));
    };
    const endPan = (e) => {
        pointersRef.current.delete(e.pointerId);
        if (pointersRef.current.size < 2)
            pinchRef.current = null;
        if (pointersRef.current.size === 0) {
            panRef.current = null;
            setPanning(false);
        }
        else if (pointersRef.current.size === 1) {
            // Dropped from pinch back to one finger — resume panning from where it is.
            const [p] = [...pointersRef.current.values()];
            panRef.current = { startX: p.x, startY: p.y, ox: viewRef.current.x, oy: viewRef.current.y };
        }
    };
    // Trackpad / mouse wheel gestures. A native, non-passive listener so we can
    // preventDefault the page scroll/zoom. Browsers report a trackpad PINCH as a
    // wheel event with ctrlKey — so ctrl/⌘+wheel = zoom-at-cursor, plain wheel =
    // two-finger pan. (React's onWheel can't be non-passive, hence the effect.)
    useEffect(() => {
        const vp = containerRef.current;
        if (!vp)
            return undefined;
        const onWheel = (e) => {
            if (e.target?.closest('.oc-canvas-tools, .oc-rail'))
                return;
            e.preventDefault();
            let dy = e.deltaY;
            if (e.deltaMode === 1)
                dy *= 16; // lines → ~px
            if (e.ctrlKey || e.metaKey) {
                // Pinch / ⌘-wheel → zoom toward the cursor. exp() keeps it smooth and
                // symmetric; clamp the per-event delta so a mouse notch isn't a huge jump.
                const factor = Math.exp(-Math.max(-60, Math.min(60, dy)) * 0.0016);
                zoomAt(e.clientX, e.clientY, factor);
            }
            else {
                let dx = e.deltaX;
                if (e.deltaMode === 1)
                    dx *= 16;
                setView((v) => ({ ...v, x: v.x - dx, y: v.y - dy }));
            }
        };
        vp.addEventListener('wheel', onWheel, { passive: false });
        return () => vp.removeEventListener('wheel', onWheel);
    }, [zoomAt]);
    // Auto-fit once when a sizeable chart first loads, so a big import lands
    // fully in view instead of running off the edge.
    const didFitRef = useRef(false);
    useEffect(() => {
        if (didFitRef.current || contacts.length + suggestions.length <= 6)
            return undefined;
        // Retry across frames until the viewport actually has a width — a fixed
        // timeout races the flex layout and fits at a zero-width viewport.
        let raf = 0;
        let tries = 0;
        const attempt = () => {
            const vp = containerRef.current;
            if (vp && vp.clientWidth > 40) {
                didFitRef.current = true;
                fitView(false);
                return;
            }
            if (tries++ < 30)
                raf = requestAnimationFrame(attempt);
        };
        const t = setTimeout(attempt, 40);
        return () => { clearTimeout(t); if (raf)
            cancelAnimationFrame(raf); };
    }, [contacts.length, suggestions.length, fitView]);
    // Optimistic updates: local state that can be modified immediately on drop
    const [optimisticUpdates, setOptimisticUpdates] = useState({});
    // Optimistically removed (deleted / merged-away) contact ids
    const [hiddenIds, setHiddenIds] = useState(new Set());
    // Armed delete disarms itself — an armed ✕ shouldn't survive a wander-off
    useEffect(() => {
        if (!armedDeleteId)
            return;
        const t = setTimeout(() => setArmedDeleteId(null), 3000);
        return () => clearTimeout(t);
    }, [armedDeleteId]);
    // Merge optimistic updates + hides with server contacts
    const effectiveContacts = useMemo(() => {
        let list = contacts;
        if (hiddenIds.size > 0)
            list = list.filter((c) => !hiddenIds.has(c.id));
        if (Object.keys(optimisticUpdates).length === 0)
            return list;
        return list.map((c) => (optimisticUpdates[c.id] ? { ...c, ...optimisticUpdates[c.id] } : c));
    }, [contacts, optimisticUpdates, hiddenIds]);
    const byId = useMemo(() => new Map(effectiveContacts.map((c) => [c.id, c])), [effectiveContacts]);
    // Bench vs tree: stashed contacts live in the tray, not the hierarchy
    const benched = useMemo(() => effectiveContacts.filter((c) => !!c.stashedAt), [effectiveContacts]);
    const placed = useMemo(() => effectiveContacts.filter((c) => !c.stashedAt), [effectiveContacts]);
    const tree = useMemo(() => buildTree(placed), [placed]);
    // Focus mode: clicking a person hides everyone NOT on their branch (their
    // ancestor path + their whole subtree stay visible), so you drill in.
    const focusHidden = useMemo(() => {
        const empty = new Set();
        if (!focusId || !byId.has(focusId))
            return empty;
        const childrenOf = new Map();
        for (const c of placed) {
            if (c.reportsToId) {
                const arr = childrenOf.get(c.reportsToId) || [];
                arr.push(c.id);
                childrenOf.set(c.reportsToId, arr);
            }
        }
        const keep = new Set([focusId]);
        let cur = byId.get(focusId)?.reportsToId || null; // ancestors (context path)
        while (cur) {
            keep.add(cur);
            cur = byId.get(cur)?.reportsToId || null;
        }
        const stack = [focusId]; // descendants (the branch)
        while (stack.length) {
            const id = stack.pop();
            for (const k of childrenOf.get(id) || [])
                if (!keep.has(k)) {
                    keep.add(k);
                    stack.push(k);
                }
        }
        const hidden = new Set();
        for (const c of placed)
            if (!keep.has(c.id))
                hidden.add(c.id);
        return hidden;
    }, [focusId, placed, byId]);
    // The tree actually rendered — pruned by collapse + focus.
    const visibleTree = useMemo(() => buildVisibleTree(tree, collapsed, focusHidden), [tree, collapsed, focusHidden]);
    const toggleCollapse = useCallback((id) => {
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(id))
                next.delete(id);
            else
                next.add(id);
            return next;
        });
    }, []);
    const focusNode = useCallback((node) => {
        setFocusId((cur) => (cur === node.id ? null : node.id)); // click again to release
    }, []);
    const expandAll = useCallback(() => { setCollapsed(new Set()); setFocusId(null); }, []);
    /*
     * Leaving focus is its OWN action, separate from "Expand all".
     *
     * Collapsing a branch by hand and isolating someone are two different
     * things a reader can be doing at once, and the one control that undid both
     * was labelled for the other one — "⤢ Expand all", with "clear focus" only
     * in its hover title. See `focusNote` for why that mattered.
     */
    const clearFocus = useCallback(() => setFocusId(null), []);
    /*
     * A focus asked for from outside. Keyed on the NONCE alone: the same id
     * arriving twice is two separate requests, and the second must land even
     * though nothing about the id changed. An unknown id is ignored rather than
     * focusing nothing — a directory row for someone not ON the chart (benched,
     * or a contact nobody has placed yet) should leave the canvas as it was.
     */
    const focusNonceRef = useRef(null);
    useEffect(() => {
        if (!focusRequest)
            return;
        if (focusNonceRef.current === focusRequest.nonce)
            return;
        focusNonceRef.current = focusRequest.nonce;
        const id = focusRequest.id;
        if (id !== null && !byId.has(id))
            return;
        setFocusId(id);
    }, [focusRequest, byId]);
    // Auto-recenter: after a collapse/expand or focus change, smoothly re-fit the
    // now-visible chart. Skips the initial mount (the load auto-fit owns that).
    const refitRef = useRef(false);
    useEffect(() => {
        if (!refitRef.current) {
            refitRef.current = true;
            return;
        }
        const raf = requestAnimationFrame(() => fitView(true));
        return () => cancelAnimationFrame(raf);
    }, [collapsed, focusId, fitView]);
    // Discovered (ghost) structure, laid out top-down beneath the confirmed tree.
    const ghostTree = useMemo(() => buildGhostTree(suggestions), [suggestions]);
    // Connector-shaped mirror (id === the DOM data-contact-id) for the dashed lines.
    const ghostConnectTree = useMemo(() => {
        const toConnect = (n) => ({ id: n.key, children: n.children.map(toConnect) });
        return ghostTree.map(toConnect);
    }, [ghostTree]);
    // Placement is drag-enabled only when the host wired a placement handler.
    const canPlaceGhosts = !!onPlaceSuggestion;
    // For touch devices: use longer delay before starting drag
    // CRM-16: keyboard drag — Space/Enter on the grip picks a card up, arrows
    // move it, Space/Enter drops it on the focused target. Paired with the
    // focusable card content (Enter opens the editor) for full no-mouse access.
    const keyboardSensor = useSensor(KeyboardSensor);
    const pointerSensor = useSensor(PointerSensor, {
        activationConstraint: { distance: 8 },
    });
    const touchSensor = useSensor(TouchSensor, {
        activationConstraint: { delay: 250, tolerance: 5 },
    });
    const sensors = useSensors(pointerSensor, touchSensor, keyboardSensor);
    // Find the active node for drag overlay (tree or bench)
    const activeNode = useMemo(() => {
        if (!activeId)
            return null;
        const find = (nodes) => {
            for (const n of nodes) {
                if (n.id === activeId)
                    return n;
                const hit = find(n.children);
                if (hit)
                    return hit;
            }
            return null;
        };
        return find(tree) || benched.find((c) => c.id === activeId) || null;
    }, [activeId, tree, benched]);
    // A ghost being dragged (id is `g-<suggestionId>`) — for the drag overlay.
    const activeGhost = useMemo(() => (activeId && activeId.startsWith('g-') ? suggestions.find((s) => `g-${s.id}` === activeId) || null : null), [activeId, suggestions]);
    // Compute blocked drop targets (can't drop on self or descendants)
    const blocked = useMemo(() => {
        if (!activeNode)
            return new Set();
        const asTree = activeNode;
        return (asTree.children ? descendantIds(asTree) : new Set()).add(activeNode.id);
    }, [activeNode]);
    /** Run a lifecycle callback with optimistic updates + revert-on-reject. */
    const act = async (apply, revert, run) => {
        apply();
        setBusy(true);
        setError(null);
        try {
            await run();
        }
        catch (e) {
            console.error('[OrgChart] action error:', e);
            revert();
            setError(e instanceof Error ? e.message : String(e));
        }
        finally {
            setBusy(false);
        }
    };
    const patchContact = (id, patch) => setOptimisticUpdates((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
    const unpatchContact = (id) => setOptimisticUpdates((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
    });
    /** Optimistically re-parent a contact's reports to its own manager. */
    const liftChildren = (id) => {
        const parent = byId.get(id);
        const newManager = parent?.reportsToId || null;
        const childIds = [];
        for (const c of effectiveContacts) {
            if (c.reportsToId === id) {
                childIds.push(c.id);
                patchContact(c.id, { reportsToId: newManager });
            }
        }
        return () => childIds.forEach(unpatchContact);
    };
    const reparent = (contactId, newManagerId) => act(
    // Placing clears the bench flag — one verb for both drags
    () => patchContact(contactId, { reportsToId: newManagerId, stashedAt: null }), () => unpatchContact(contactId), () => onReparent(contactId, newManagerId));
    const doDelete = (contactId) => {
        if (!onDelete)
            return;
        setArmedDeleteId(null);
        const revertChildren = liftChildren(contactId);
        act(() => setHiddenIds((prev) => new Set(prev).add(contactId)), () => {
            revertChildren();
            setHiddenIds((prev) => {
                const next = new Set(prev);
                next.delete(contactId);
                return next;
            });
        }, () => onDelete(contactId));
    };
    const doStash = (contactId) => {
        if (!onStash)
            return;
        const revertChildren = liftChildren(contactId);
        act(() => patchContact(contactId, { stashedAt: new Date().toISOString(), reportsToId: null }), () => {
            revertChildren();
            unpatchContact(contactId);
        }, () => onStash(contactId));
    };
    const doMerge = (targetId, sourceId) => {
        if (!onMerge)
            return;
        setPendingDrop(null);
        // Source's reports go to the merge survivor
        const childIds = [];
        for (const c of effectiveContacts) {
            if (c.reportsToId === sourceId) {
                childIds.push(c.id);
                patchContact(c.id, { reportsToId: targetId });
            }
        }
        act(() => setHiddenIds((prev) => new Set(prev).add(sourceId)), () => {
            childIds.forEach(unpatchContact);
            setHiddenIds((prev) => {
                const next = new Set(prev);
                next.delete(sourceId);
                return next;
            });
        }, () => onMerge(targetId, sourceId));
    };
    // Verify / Field: stamp the fields locally NOW (the pips flip on the
    // click), persist behind, revert on reject. Per-card in-flight scope.
    const [verifyBusy, setVerifyBusy] = useState(new Set());
    const doVerify = (contactId, status, fields) => {
        if (!onVerify || fields.length === 0)
            return;
        const cur = byId.get(contactId);
        if (!cur)
            return;
        const prevProv = { ...(cur.provenance || {}) };
        const now = new Date().toISOString();
        const nextProv = { ...prevProv };
        for (const f of fields)
            nextProv[f] = { ...(prevProv[f] || {}), status, guessed: false, confidence: 100, at: now };
        setVerifyBusy((s) => new Set(s).add(contactId));
        act(() => patchContact(contactId, { provenance: nextProv }), () => patchContact(contactId, { provenance: prevProv }), () => onVerify(contactId, status, fields)).finally(() => setVerifyBusy((s) => { const n = new Set(s); n.delete(contactId); return n; }));
    };
    // A provenance patch is redundant once the server row carries claims at
    // least as strong — drop it then, so later server changes show through.
    useEffect(() => {
        setOptimisticUpdates((prev) => {
            let changed = false;
            const next = { ...prev };
            for (const [id, patch] of Object.entries(prev)) {
                if (!patch.provenance)
                    continue;
                const server = contacts.find((c) => c.id === id);
                if (!server)
                    continue;
                const sp = server.provenance || {};
                const settled = Object.entries(patch.provenance).every(([f, e]) => {
                    const want = PROV_RANK[String(e?.status)] ?? 0;
                    const have = PROV_RANK[String(sp[f]?.status)] ?? -1;
                    return have >= want;
                });
                if (settled) {
                    const { provenance: _dropped, ...rest } = patch;
                    next[id] = rest;
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [contacts]);
    const handleDragStart = (event) => {
        setActiveId(String(event.active.id));
        setArmedDeleteId(null);
    };
    const handleDragOver = (event) => {
        setOverId(event.over ? String(event.over.id) : null);
    };
    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveId(null);
        setOverId(null);
        if (!over || busy)
            return;
        const draggedId = String(active.id);
        const dropId = String(over.id);
        // Placing a DISCOVERED person (ghost): drop it onto a real manager card, or
        // onto the top-level zone. Creates the contact wired to that manager.
        if (draggedId.startsWith('g-')) {
            const suggestionId = draggedId.slice(2);
            // Drop onto the bench = validate-don't-place (create real, park unplaced).
            if (dropId === 'bench-drop-zone') {
                if (onBenchSuggestion)
                    benchSuggestion(suggestionId);
                return;
            }
            if (!onPlaceSuggestion)
                return;
            if (dropId === 'root-drop-zone') {
                placeSuggestion(suggestionId, null);
            }
            else if (dropId.startsWith('drop-')) {
                const targetId = dropId.replace('drop-', '');
                const target = byId.get(targetId); // must resolve to a REAL, placed contact
                if (target && !target.stashedAt)
                    placeSuggestion(suggestionId, targetId);
            }
            return;
        }
        // Check if dropping on root zone
        if (dropId === 'root-drop-zone') {
            reparent(draggedId, null);
            return;
        }
        // Park on the bench
        if (dropId === 'bench-drop-zone') {
            const dragged = byId.get(draggedId);
            if (onStash && dragged && !dragged.stashedAt)
                doStash(draggedId);
            return;
        }
        // Check if dropping on a contact card
        if (dropId.startsWith('drop-')) {
            const targetId = dropId.replace('drop-', '');
            // Can't drop on self or descendants
            if (blocked.has(targetId) || targetId === draggedId)
                return;
            const dragged = byId.get(draggedId);
            const target = byId.get(targetId);
            if (!dragged || !target)
                return;
            if (target.stashedAt) {
                // A benched contact can't be a manager — a drop here is merge intent
                if (onMerge)
                    setPendingDrop({ sourceId: draggedId, targetId, allowReport: false });
                return;
            }
            if (onMerge && looksLikeDuplicate(dragged, target)) {
                setPendingDrop({ sourceId: draggedId, targetId, allowReport: true });
                return;
            }
            reparent(draggedId, targetId);
        }
    };
    const handleDragCancel = () => {
        setActiveId(null);
        setOverId(null);
    };
    // Nothing to show only when there are neither confirmed contacts NOR
    // suggestions. With suggestions but no contacts, we still render the ghost
    // tree so a freshly-discovered agency isn't blank.
    if (contacts.length === 0 && suggestions.length === 0)
        return null;
    const isOverRoot = overId === 'root-drop-zone';
    const isOverBench = overId === 'bench-drop-zone';
    // Check if org chart is flat (no hierarchy established)
    const hasHierarchy = tree.some((root) => root.children.length > 0);
    const isFlat = !hasHierarchy && tree.length > 1;
    const pendingSource = pendingDrop ? byId.get(pendingDrop.sourceId) : null;
    const pendingTarget = pendingDrop ? byId.get(pendingDrop.targetId) : null;
    // Dragging a benched card? The bench zone shouldn't light up for its own cards.
    const draggingBenched = !!(activeId && byId.get(activeId)?.stashedAt);
    const showBench = !!onStash || benched.length > 0;
    return (_jsxs(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragStart: handleDragStart, onDragOver: handleDragOver, onDragEnd: handleDragEnd, onDragCancel: handleDragCancel, children: [_jsxs("div", { className: `oc-container ${fullscreen ? 'oc-fullscreen' : ''}`, children: [isFlat && !activeId && (_jsxs("div", { className: "oc-setup-hint", children: [_jsx(GripVertical, { size: 16 }), _jsxs("span", { children: [_jsx("strong", { children: "Set up reporting structure:" }), " Drag a contact onto another to make them report to that person"] })] })), activeId && _jsx(RootDropZone, { isOver: isOverRoot }), error && _jsx("div", { className: "oc-error", children: error }), _jsxs("div", { className: "oc-canvas-row", children: [_jsxs("div", { className: `oc-scroll ${panning ? 'oc-panning' : ''} ${dense ? 'oc-dense' : ''}`, ref: containerRef, onPointerDown: onCanvasPointerDown, onPointerMove: onCanvasPointerMove, onPointerUp: endPan, onPointerCancel: endPan, onPointerLeave: endPan, children: [focusId && byId.has(focusId) && (_jsxs("div", { className: "oc-focus-note", role: "status", onPointerDown: (e) => e.stopPropagation(), children: [_jsxs("span", { className: "oc-focus-note-text", children: ["Showing ", _jsx("strong", { children: fullName(byId.get(focusId)) }), "\u2019s branch", focusHidden.size > 0 && ` — ${focusHidden.size} ${focusHidden.size === 1 ? 'person' : 'people'} hidden`] }), _jsx("button", { type: "button", className: "oc-focus-note-btn", onClick: clearFocus, children: "Show everyone" })] })), _jsxs("div", { className: "oc-canvas-tools", onPointerDown: (e) => e.stopPropagation(), children: [_jsx("button", { type: "button", className: "oc-tool", title: "Zoom out", onClick: () => zoomBy(0.8), children: "\u2212" }), _jsxs("span", { className: "oc-tool-z", title: "Zoom level", children: [Math.round(view.z * 100), "%"] }), _jsx("button", { type: "button", className: "oc-tool", title: "Zoom in", onClick: () => zoomBy(1.25), children: "+" }), _jsx("span", { className: "oc-tool-sep" }), _jsx("button", { type: "button", className: "oc-tool oc-tool-wide", title: "Fit / recenter the chart in view", onClick: () => fitView(true), children: "Fit" }), _jsx("button", { type: "button", className: "oc-tool oc-tool-wide", title: "Reset to 100%", onClick: resetView, children: "1:1" }), _jsx("span", { className: "oc-tool-sep" }), _jsx("button", { type: "button", className: `oc-tool oc-tool-wide ${dense ? 'oc-tool-on' : ''}`, title: "Compact card density", onClick: () => setDense((d) => !d), children: "Compact" }), (collapsed.size > 0 || focusId) && (_jsxs(_Fragment, { children: [_jsx("span", { className: "oc-tool-sep" }), _jsx("button", { type: "button", className: "oc-tool oc-tool-wide oc-tool-accent", title: "Expand every collapsed branch and clear focus", onClick: expandAll, children: "\u2922 Expand all" })] }))] }), _jsxs("div", { className: "oc-canvas", ref: canvasRef, style: { transform: `translate(${view.x}px, ${view.y}px) scale(${view.z})`, transformOrigin: '0 0' }, children: [_jsx(ConnectorLines, { containerRef: canvasRef, tree: visibleTree, zoom: view.z }), ghostConnectTree.length > 0 && (_jsx(ConnectorLines, { containerRef: canvasRef, tree: ghostConnectTree, zoom: view.z, dashed: true })), _jsxs("div", { className: "oc-canvas-content", children: [_jsx("ul", { className: "oc-tree", children: visibleTree.map((node) => (_jsx(ContactCard, { node: node, activeId: activeId, overId: overId, focusId: focusId, blocked: blocked, relationshipLabels: relationshipLabels, armedDeleteId: armedDeleteId, onArmDelete: setArmedDeleteId, onDelete: onDelete ? doDelete : undefined, onAddContact: onAddContact, onContactClick: onContactClick, onToggleCollapse: toggleCollapse, onFocus: focusNode, verifyBusy: verifyBusy, onVerify: onVerify ? doVerify : undefined }, node.id))) }), ghostTree.length > 0 && (_jsxs("div", { className: "oc-ghost-layer", children: [_jsxs("div", { className: "oc-ghost-banner", children: [_jsx("span", { className: "oc-ghost-diamond", children: "\u25C7" }), "Discovered structure \u2014 not added yet.", canPlaceGhosts ? ' Drag onto a manager to place, onto the bench to validate-only, or ' : ' ', "use ", _jsx("strong", { children: "Add" }), onBenchSuggestion ? ' / ' : '', onBenchSuggestion ? _jsx("strong", { children: "Bench" }) : '', " / ", _jsx("strong", { children: "Merge" }), " on each."] }), _jsx("ul", { className: "oc-tree oc-ghost-tree", children: ghostTree.map((node) => (_jsx(GhostCard, { node: node, draggable: canPlaceGhosts, activeId: activeId, busyId: suggBusyId, onConfirm: onConfirmSuggestion ? (id) => runSuggestion(id, onConfirmSuggestion) : undefined, onMerge: onMergeSuggestion ? (id) => runSuggestion(id, onMergeSuggestion) : undefined, onReject: onRejectSuggestion ? (id) => runSuggestion(id, onRejectSuggestion) : undefined, onBench: onBenchSuggestion ? (id) => runSuggestion(id, onBenchSuggestion) : undefined }, node.key))) })] }))] })] })] }), _jsx(SuggestionRail, { suggestions: suggestions, onConfirm: onConfirmSuggestion, onMerge: onMergeSuggestion, onReject: onRejectSuggestion, onBench: onBenchSuggestion, onAcceptAll: onAcceptAllSuggestions })] }), showBench && (_jsx(BenchTray, { benched: benched, isOver: isOverBench && !draggingBenched, droppable: !!onStash && !draggingBenched, activeId: activeId, armedDeleteId: armedDeleteId, onArmDelete: setArmedDeleteId, onDelete: onDelete ? doDelete : undefined, onContactClick: onContactClick, verifyBusy: verifyBusy, onVerify: onVerify ? doVerify : undefined })), _jsxs("div", { className: "oc-footer", children: [_jsxs("div", { className: "oc-hint", children: [_jsx(GripVertical, { size: 12 }), _jsxs("span", { children: ["Drag contacts onto each other to set reporting structure", onContactClick ? ', or click to edit' : ''] })] }), _jsxs("div", { className: "oc-legend", children: [Object.entries(relationshipLabels).map(([key, label]) => (_jsxs("span", { className: "oc-legend-item", children: [_jsx("span", { className: `oc-legend-dot oc-rel-${key}` }), " ", label] }, key))), _jsxs("span", { className: "oc-legend-item", children: [_jsx("span", { className: "oc-badge oc-badge-dm", children: "DM" }), " Decision maker"] }), _jsx("span", { className: "oc-legend-sep", "aria-hidden": "true" }), PIP_ORDER.map((k) => (_jsxs("span", { className: "oc-legend-item", children: [_jsx("span", { className: `oc-pip oc-pip-${k} oc-pip-static`, children: _jsx(PipGlyph, { kind: k }) }), " ", PIP_LABEL[k]] }, k))), _jsx(Hint, { label: "Who set what", text: (_jsxs(_Fragment, { children: [_jsx("span", { children: "Each detail on a card remembers who set it. Hover or tap a pip to see the fields, their confidence and the source." }), onVerify && (_jsxs("span", { className: "hnt-def", children: [_jsx("b", { children: "Verify" }), " marks a card's unchecked details as checked by you. ", _jsx("b", { children: "Field" }), " means someone in the account confirmed them. Both are one click; an assistant can never undo either."] }))] })) })] })] }), pendingDrop && pendingSource && pendingTarget && (_jsx("div", { className: "oc-merge-backdrop", onClick: () => setPendingDrop(null), children: _jsxs("div", { className: "oc-merge-popover", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "oc-merge-title", children: [_jsx(Merge, { size: 14 }), _jsx("span", { children: pendingDrop.allowReport
                                                ? `${fullName(pendingSource)} looks like a duplicate of ${fullName(pendingTarget)}`
                                                : `Merge ${fullName(pendingSource)} into ${fullName(pendingTarget)}?` })] }), _jsxs("div", { className: "oc-merge-actions", children: [pendingDrop.allowReport && (_jsxs("button", { className: "oc-merge-btn", onClick: () => {
                                                const { sourceId, targetId } = pendingDrop;
                                                setPendingDrop(null);
                                                reparent(sourceId, targetId);
                                            }, children: ["Reports to ", fullName(pendingTarget)] })), _jsxs("button", { className: "oc-merge-btn oc-merge-btn-primary", onClick: () => doMerge(pendingDrop.targetId, pendingDrop.sourceId), children: ["Merge into ", fullName(pendingTarget)] }), _jsx("button", { className: "oc-merge-btn oc-merge-btn-cancel", onClick: () => setPendingDrop(null), children: "Cancel" })] }), _jsxs("div", { className: "oc-merge-note", children: ["Merging keeps ", fullName(pendingTarget), "'s details, absorbs ", fullName(pendingSource), "'s notes and coverage, and moves ", fullName(pendingSource), " to the trash."] })] }) }))] }), _jsx(DragOverlay, { dropAnimation: null, children: activeNode ? _jsx(DragOverlayCard, { node: activeNode }) : activeGhost ? _jsx(GhostOverlayCard, { s: activeGhost }) : null })] }));
}
export default OrgChart;
